import { Icon } from '@buggy/shared'
import { Button } from 'antd'
import { isUndefined } from 'lodash-es'
import { type FunctionComponent } from 'react'
import styled from 'styled-components'
import { PostImageWrapper } from '@/components/common/image/post-image'
import PostCard, {
  PostCardContent,
  PostCardOtherInfo,
  PostCardTitle,
} from '@/components/common/post-card'
import PostCardStatistic from '@/components/common/post/post-card-statistic'
import type { Post } from '@/types/schema/directory/collaboration-record'
import { convertPlatformShortcode } from '@/utils/convert-platform'
import getPostThumbnailUrls from '@/utils/kol/get-post-thumbnail-urls'

interface CollaborationRecordPostProps {
  value?: Post
  kolId: string
  kolName: string
  onDelete: VoidFunction
}

const CollaborationRecordPost: FunctionComponent<
  CollaborationRecordPostProps
> = ({ value, kolId, kolName, onDelete }) => {
  const platformType = convertPlatformShortcode(value?.platform).type

  if (!value || isUndefined(platformType)) {
    return null
  }

  return (
    <Card
      actionBlock={
        <DeleteButton
          icon={<Icon fontSize={16} name='trash-2' />}
          size='small'
          type='text'
          onClick={onDelete}
        />
      }
      canGiveFeedback={false}
      commentCount={value.comment_count}
      isDisableLike={value.is_disable_like ?? false}
      kolName={kolName}
      kolUuid={kolId}
      likeCount={value.like_count}
      platformShortCode={value.platform}
      playCount={value.play_count}
      postUrl={value.url}
      publishedAt={value.publish_time}
      shareCount={value.share_count}
      showKolInfo={false}
      thumbnailUrls={getPostThumbnailUrls({
        platformType,
        crawledID: value.crawled_id ?? '',
        postID: value.post_id,
        igPostId: value.ig_post_id ?? '',
        thumbnail: value.thumbnail_url,
      })}
      title={value.title.trim() + value.content.trim()}
      titleLineClamp={1}
      viewCount={value.view_count}
    />
  )
}

const DeleteButton = styled(Button)`
  padding: 0;
  height: 16px;
  line-height: 16px;

  &.ant-btn-sm.ant-btn-icon-only {
    width: 16px;
  }
`

const Card = styled(PostCard)`
  & {
    flex-direction: row;
    box-shadow: none;
    border-radius: 4px;
    gap: 12px;
    border: 1px solid ${({ theme }): string => theme.colors.border.border};
    background: ${({ theme }): string => theme.colors.background.white};
    padding: 12px;
  }

  ${PostImageWrapper} {
    width: 120px;
    height: 90px;
    border-radius: 3px;
    cursor: default;
  }

  ${PostCardContent} {
    padding: 0;
    justify-content: center;
    gap: 8px;

    ${PostCardOtherInfo} {
      margin: 0;
      align-items: center;
    }

    ${PostCardTitle} {
      font-size: 16px;
      font-weight: 400;
      margin: 0;
      cursor: default;
      word-break: break-all;
    }

    ${PostCardStatistic} {
      margin: 0;
      flex-grow: 0;
    }
  }
`

export default CollaborationRecordPost
